/**
 * Per-year vacation computation.
 *
 * Computes entitlement, carryover, taken, and balance for a single year.
 * Carryover is *computed* (not stored) — we derive it from the previous year's
 * remaining balance.  This guarantees correctness without sync issues.
 */

import { round2 } from "@/utils/workTimeUtils";

// ─── Types ──────────────────────────────────────────────────────────

export interface YearlyVacationSummary {
  year: number;
  /** Annual grant for this year (prorated for partial years) */
  entitlement: number;
  /** Unused hours carried in from the previous year */
  carryover: number;
  /** Hours consumed this year */
  taken: number;
  /** entitlement + carryover - taken */
  available: number;
  /** Prorated entitlement earned up to current month (HR/termination view) */
  accrued: number;
}

interface VacationTransaction {
  transaction_type: string;
  transaction_date: string;
  days_amount: number; // stores HOURS for hours-based employees
}

// ─── Computation ────────────────────────────────────────────────────

/**
 * Compute the vacation summary for a single year.
 *
 * @param year                    The year to compute
 * @param contractStart           Employee's contract start date
 * @param annualVacationHours     Full-year vacation grant (e.g. 20 h)
 * @param transactions            All "taken" transactions for this year
 * @param previousYearCarryover   The `available` balance from the previous year (0 for the first year)
 * @param currentDate             "now" — used for accrual proration
 * @param unpaidLeaveMonths       "YYYY-MM" strings for months with unpaid leave (reduce entitlement)
 */
export function computeYearlyVacation(
  year: number,
  contractStart: Date,
  annualVacationHours: number,
  transactions: VacationTransaction[],
  previousYearCarryover: number,
  currentDate: Date = new Date(),
  unpaidLeaveMonths: string[] = []
): YearlyVacationSummary {
  const contractYear = contractStart.getFullYear();

  // Before contract started → all zeros
  if (year < contractYear) {
    return { year, entitlement: 0, carryover: 0, taken: 0, available: 0, accrued: 0 };
  }

  // Count unpaid leave months in this year
  const yearPrefix = `${year}-`;
  const unpaidInYear = unpaidLeaveMonths.filter((m) => m.startsWith(yearPrefix)).length;

  // Entitlement
  let entitlement: number;
  if (year === contractYear) {
    // Prorate: count months from contract month to December, minus unpaid
    const contractMonth = contractStart.getMonth() + 1; // 1-based
    const monthsInYear = 12 - contractMonth + 1 - unpaidInYear;
    entitlement = round2((Math.max(0, monthsInYear) / 12) * annualVacationHours);
  } else {
    const paidMonths = 12 - unpaidInYear;
    entitlement = round2((Math.max(0, paidMonths) / 12) * annualVacationHours);
  }

  // Carryover: 0 for the first year, otherwise from the previous year
  const carryover = year === contractYear ? 0 : round2(previousYearCarryover);

  // Taken: sum of "taken" transactions within this year
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year + 1}-01-01`;
  const taken = round2(
    transactions
      .filter(
        (t) =>
          t.transaction_type === "taken" &&
          t.transaction_date >= yearStart &&
          t.transaction_date < yearEnd
      )
      .reduce((sum, t) => sum + (t.days_amount || 0), 0)
  );

  const available = round2(entitlement + carryover - taken);

  // Accrued: prorated entitlement through the current month
  let accrued: number;
  const isCurrentYear = year === currentDate.getFullYear();
  if (isCurrentYear) {
    const currentMonth = currentDate.getMonth() + 1; // 1-based
    accrued = round2((currentMonth / 12) * entitlement);
  } else {
    accrued = entitlement; // past years are fully accrued
  }

  return { year, entitlement, carryover, taken, available, accrued };
}

/**
 * Convenience: compute vacation for `targetYear`, automatically deriving
 * the carryover by computing the previous year first.
 *
 * @param unpaidLeaveMonths  "YYYY-MM" strings — months with unpaid leave reduce entitlement
 */
export function computeVacationWithCarryover(
  targetYear: number,
  contractStart: Date,
  annualVacationHours: number,
  allTransactions: VacationTransaction[],
  currentDate: Date = new Date(),
  unpaidLeaveMonths: string[] = []
): YearlyVacationSummary {
  const contractYear = contractStart.getFullYear();

  if (targetYear <= contractYear) {
    // First year or earlier — no carryover
    return computeYearlyVacation(
      targetYear,
      contractStart,
      annualVacationHours,
      allTransactions,
      0,
      currentDate,
      unpaidLeaveMonths
    );
  }

  // Compute previous year to get carryover
  const prevSummary = computeVacationWithCarryover(
    targetYear - 1,
    contractStart,
    annualVacationHours,
    allTransactions,
    currentDate,
    unpaidLeaveMonths
  );

  return computeYearlyVacation(
    targetYear,
    contractStart,
    annualVacationHours,
    allTransactions,
    prevSummary.available,
    currentDate,
    unpaidLeaveMonths
  );
}
